// Shared classification for wallet-extension signing failures.
//
// A *user rejection* (the person clicked "reject"/"cancel" in the wallet popup) is a
// benign outcome — callers treat it as a no-op cancel and may return undefined.
// Any *other* failure — a locked extension, a stale/disconnected session, an RPC or
// timeout error — MUST propagate, so that:
//   1. runExternalSigningRequest can retry after the user unlocks and focus returns, and
//   2. the purchase UI surfaces a real error instead of silently resetting the Buy button.
//
// Historically every provider's signPurchase swallowed ALL errors and returned undefined,
// which silently broke buying whenever a wallet merely needed an unlock/reconnect. This
// helper lets each provider keep the clean-cancel behaviour while rethrowing real errors.
// See run-external-signing-request.ts for the retry this unblocks.
export function isUserRejection(error: any): boolean {
	if (!error) return false

	const message = String(
		error?.message ?? error?.error?.message ?? error?.reason ?? error ?? ''
	).toLowerCase()
	// TAP Wallet 1.0.47 also emits code 4001 when the wallet has no account.
	// That is a setup failure, not a user cancellation, and must remain visible.
	if (message.includes('must has at least one account') || message.includes('has no account')) {
		return false
	}

	// EIP-1193 / most BTC wallet extensions use 4001 for "user rejected request".
	const code = error?.code ?? error?.error?.code ?? error?.data?.code
	if (String(code) === '4001') return true

	return (
		message.includes('user rejected') ||
		message.includes('rejected by user') ||
		message.includes('user canceled') ||
		message.includes('user cancelled') ||
		message.includes('canceled by user') ||
		message.includes('cancelled by user') ||
		message.includes('request was canceled') ||
		message.includes('request was cancelled') ||
		message.includes('user denied') ||
		message.includes('denied by user') ||
		message.includes('user declined') ||
		message.includes('declined by user') ||
		message.includes('user refused') ||
		message.includes('refused by user')
	)
}
